import { useState } from 'react'
import { supabase } from '../supabase'
import { useNavigate, Link } from 'react-router-dom'
import { useBrand } from '../hooks/useBrand'
import CustomerAuthHero from '../components/CustomerAuthHero'

// ── Brand tokens — standalone public page, no CSS vars ──
const NAVY  = '#0B1F3A'
const TEAL  = '#2DD4B0'
const MUTED = '#6B7280'
const BORDER= '#E5E7EB'
const BG    = '#F8FAFC'

const inp = {
  width: '100%', border: '1.5px solid ' + BORDER, borderRadius: '10px',
  padding: '11px 14px', fontSize: '14px', outline: 'none',
  fontFamily: 'Space Grotesk, -apple-system, sans-serif',
  boxSizing: 'border-box', color: '#111827', background: '#fff',
  transition: 'border-color 0.15s', WebkitAppearance: 'none',
}
const iF = e => { e.target.style.borderColor = TEAL }
const iB = e => { e.target.style.borderColor = BORDER }

function Lbl({ children }) {
  return <label style={{ fontSize: '12px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '6px' }}>{children}</label>
}

function ErrBox({ msg }) {
  if (!msg) return null
  return (
    <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', padding: '10px 12px', marginBottom: '14px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: '1px' }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      <span style={{ fontSize: '12.5px', color: '#B91C1C', lineHeight: 1.5 }}>{msg}</span>
    </div>
  )
}

function PwInput({ value, onChange, onKeyDown }) {
  const [show, setShow] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <input type={show ? 'text' : 'password'} value={value} onChange={onChange}
        onKeyDown={onKeyDown} placeholder="••••••••"
        style={{ ...inp, paddingRight: '44px' }} onFocus={iF} onBlur={iB} />
      <button type="button" onClick={() => setShow(s => !s)}
        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: MUTED, fontSize: '11.5px', fontWeight: 600, padding: '2px' }}>
        {show ? 'Hide' : 'Show'}
      </button>
    </div>
  )
}

// ── Forgot password — top-level component (avoids nested component remount) ──
function ForgotPassword({ initialEmail, onBack }) {
  const [fpEmail, setFpEmail]   = useState(initialEmail || '')
  const [fpLoading, setFpLoading] = useState(false)
  const [fpSent, setFpSent]     = useState(false)
  const [fpError, setFpError]   = useState('')

  const handleForgotPassword = async () => {
    if (!fpEmail) { setFpError('Please enter your email address'); return }
    setFpLoading(true); setFpError('')
    const { error } = await supabase.auth.resetPasswordForEmail(fpEmail.trim().toLowerCase(), {
      redirectTo: 'https://app.pageconnect.io/reset-password',
    })
    if (error) { setFpError(error.message); setFpLoading(false); return }
    setFpSent(true); setFpLoading(false)
  }

  return (
    <div style={{ width: '100%', maxWidth: '420px', paddingBottom: '40px' }}>
      <button onClick={onBack}
        style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12.5px', color: MUTED, background: 'none', border: 'none', cursor: 'pointer', marginBottom: '24px', padding: 0, fontFamily: 'inherit' }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        Back to login
      </button>

      {fpSent ? (
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(45,212,176,0.1)', border: '2px solid rgba(45,212,176,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="1.8" strokeLinecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          </div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: '#111827', marginBottom: '8px' }}>Check your email</div>
          <div style={{ fontSize: '13px', color: MUTED, marginBottom: '4px' }}>We sent a reset link to</div>
          <div style={{ fontSize: '14px', fontWeight: 700, color: NAVY, marginBottom: '24px' }}>{fpEmail}</div>
          <div style={{ background: BG, borderRadius: '12px', padding: '14px 16px', marginBottom: '24px', fontSize: '12.5px', color: MUTED, lineHeight: 1.8, textAlign: 'left' }}>
            {['Click the link in the email to reset your password','Link expires in 1 hour','Check your spam folder if you don\'t see it'].map((t,i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                {t}
              </div>
            ))}
          </div>
          <button onClick={onBack}
            style={{ width: '100%', padding: '13px', borderRadius: '12px', background: NAVY, border: 'none', fontSize: '14px', fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
            Back to login
          </button>
        </div>
      ) : (
        <>
          <div style={{ fontSize: '22px', fontWeight: 800, color: '#111827', marginBottom: '6px' }}>Forgot password?</div>
          <div style={{ fontSize: '13px', color: MUTED, marginBottom: '24px', lineHeight: 1.6 }}>Enter your email and we'll send a reset link.</div>

          <ErrBox msg={fpError} />

          <div style={{ marginBottom: '16px' }}>
            <Lbl>Email address</Lbl>
            <input type="email" value={fpEmail} onChange={e => setFpEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleForgotPassword()}
              placeholder="your@email.com" autoFocus style={inp} onFocus={iF} onBlur={iB} />
          </div>

          <button onClick={handleForgotPassword} disabled={fpLoading}
            style={{ width: '100%', padding: '13px', borderRadius: '12px', background: fpLoading ? BORDER : NAVY, border: 'none', fontSize: '14px', fontWeight: 700, color: fpLoading ? '#9CA3AF' : '#fff', cursor: fpLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {fpLoading && <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'ca-spin 0.7s linear infinite' }} />}
            {fpLoading ? 'Sending...' : 'Send reset link'}
          </button>
        </>
      )}
    </div>
  )
}

export default function CustomerLogin() {
  const navigate  = useNavigate()
  const { brand } = useBrand()

  const [form, setForm]       = useState({ email: '', password: '' })
  const [error, setError]     = useState('')
  const [loading, setLoading] = useState(false)
  const [showForgot, setShowForgot] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleLogin = async () => {
    if (!form.email || !form.password) { setError('Please fill in all fields'); return }
    setLoading(true); setError('')

    const { data, error: signInError } = await supabase.auth.signInWithPassword({ email: form.email, password: form.password })
    if (signInError) { setError(signInError.message); setLoading(false); return }

    const { data: account } = await supabase.from('customer_accounts').select('id').eq('user_id', data.user.id).single()
    if (!account) {
      await supabase.auth.signOut()
      setError('No customer account found. Are you a seller? Sign in at the seller portal.')
      setLoading(false); return
    }
    navigate('/customer/portal')
  }

  const openForgot = () => { setForgotEmail(form.email); setShowForgot(true) }

  return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', fontFamily: 'Space Grotesk, -apple-system, sans-serif' }}>

      {/* Hero — 55% */}
      <div className="ca-hero" style={{ flex: '0 0 55%', display: 'none' }}>
        <CustomerAuthHero brand={brand} subtitle="Your order history, all in one place" />
      </div>

      {/* Form — flex 1 */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 32px', overflowY: 'auto', minHeight: '100vh' }}>

        {showForgot ? (
          <ForgotPassword initialEmail={forgotEmail} onBack={() => setShowForgot(false)} />
        ) : (
          <div style={{ width: '100%', maxWidth: '420px' }}>

            {/* Mobile brand */}
            <div className="ca-mobile-brand" style={{ display: 'none', textAlign: 'center', marginBottom: '28px' }}>
              {brand.logo_url
                ? <img src={brand.logo_url} alt={brand.name} style={{ height: '30px', objectFit: 'contain', margin: '0 auto', display: 'block' }} />
                : <div style={{ fontSize: '20px', fontWeight: 800, color: NAVY }}>{brand.name || 'PageConnect'}</div>
              }
            </div>

            <div style={{ fontSize: '24px', fontWeight: 800, color: '#111827', marginBottom: '4px' }}>Welcome back</div>
            <div style={{ fontSize: '13px', color: MUTED, marginBottom: '28px' }}>Sign in to view your orders and receipts</div>

            <ErrBox msg={error} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
              <div>
                <Lbl>Email address</Lbl>
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                  placeholder="your@email.com" style={inp} onFocus={iF} onBlur={iB} />
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <Lbl>Password</Lbl>
                  <button onClick={openForgot}
                    style={{ fontSize: '12px', fontWeight: 600, color: NAVY, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0, textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                    Forgot password?
                  </button>
                </div>
                <PwInput value={form.password} onChange={e => set('password', e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()} />
              </div>
            </div>

            <button onClick={handleLogin} disabled={loading}
              style={{ width: '100%', padding: '14px', borderRadius: '12px', background: loading ? BORDER : NAVY, border: 'none', fontSize: '14px', fontWeight: 700, color: loading ? '#9CA3AF' : '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'opacity 0.15s' }}>
              {loading && <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'ca-spin 0.7s linear infinite' }} />}
              {loading ? 'Signing in...' : 'Sign in'}
            </button>

            <div style={{ textAlign: 'center', fontSize: '13px', color: MUTED, marginBottom: '8px' }}>
              No account yet?{' '}
              <Link to="/customer/signup" style={{ fontWeight: 600, color: NAVY, textDecoration: 'none' }}>Create free account</Link>
            </div>
            <div style={{ textAlign: 'center', fontSize: '12px', color: MUTED, marginBottom: '8px' }}>
              Just tracking an order?{' '}
              <Link to="/track" style={{ fontWeight: 600, color: MUTED, textDecoration: 'none', borderBottom: '1px solid ' + BORDER }}>Track without account</Link>
            </div>
            <div style={{ textAlign: 'center', fontSize: '12px', color: MUTED }}>
              Seller?{' '}
              <Link to="/login" style={{ fontWeight: 600, color: MUTED, textDecoration: 'none', borderBottom: '1px solid ' + BORDER }}>Seller sign in</Link>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes ca-spin { to { transform: rotate(360deg) } }
        .ca-hero { display: flex !important; }
        @media(max-width: 768px) { .ca-hero { display: none !important; } .ca-mobile-brand { display: block !important; } }
      `}</style>
    </div>
  )
}